import { Loader, Mail, X, CheckCircle, AlertCircle } from "lucide-react";

const ResetPasswordModal = ({
  isOpen,
  onClose,
  t,
  styles,
  screenSize,
  // state
  resetEmail,
  setResetEmail,
  resetStatus,
  resetMessage,
  isResetLoading,
  // Seconds until another reset link may be requested (0 = allowed now).
  // BROWSER-WIDE: while running, no reset can be requested for ANY email —
  // the email field itself is frozen. Mirrors the server-side signed-cookie
  // cooldown; survives modal close/reopen and page refresh.
  resendCooldownSeconds = 0,
  // handlers
  onSubmit,
  onKeyPress,
}) => {
  if (!isOpen) return null;

  const cooldownActive = resendCooldownSeconds > 0;
  const submitDisabled = isResetLoading || resetStatus === "success" || cooldownActive;
  const inputDisabled = isResetLoading || resetStatus === "success" || cooldownActive;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          style={styles.modalCloseButton}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#F3F4F6";
            e.currentTarget.style.color = "#374151";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "#6B7280";
          }}
          aria-label="Close"
        >
          <X size={22} />
        </button>

        {/* Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{t.resetPassword}</h2>
          <p style={styles.modalSubtitle}>{t.resetPasswordSubtitle}</p>
        </div>

        {/* Form */}
        <div style={styles.modalForm}>
          {/* Email Input */}
          <div style={styles.modalInputGroup}>
            <label style={styles.modalLabel}>{t.resetEmailLabel || t.emailAddress}</label>
            <div style={styles.modalInputWrapper}>
              <Mail style={styles.modalIcon} />
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                onKeyPress={onKeyPress}
                placeholder={t.resetEnterEmail || t.enterEmail}
                disabled={inputDisabled}
                style={{
                  ...styles.modalInput,
                  opacity: inputDisabled ? 0.6 : 1,
                  cursor: cooldownActive ? "not-allowed" : undefined,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#084d98";
                  e.target.style.backgroundColor = "#FFFFFF";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(8, 77, 152, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E5E7EB";
                  e.target.style.backgroundColor = "#F9FAFB";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          {/* Status Message */}
          {resetStatus && (
            <div
              style={{
                ...styles.modalStatusMessage,
                ...(resetStatus === "success"
                  ? styles.modalSuccessMessage
                  : styles.modalErrorMessage),
              }}
            >
              {resetStatus === "success" ? (
                <CheckCircle
                  size={20}
                  style={{ flexShrink: 0, marginTop: "2px" }}
                />
              ) : (
                <AlertCircle
                  size={20}
                  style={{ flexShrink: 0, marginTop: "2px" }}
                />
              )}
              <span>{resetMessage}</span>
            </div>
          )}

          {/* Buttons */}
          <div style={styles.modalButtonGroup}>
            {/* Cancel */}
            <button
              type="button"
              onClick={onClose}
              disabled={isResetLoading}
              style={{
                ...styles.modalButton,
                ...styles.modalSecondaryButton,
                opacity: isResetLoading ? 0.5 : 1,
                cursor: isResetLoading ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!isResetLoading)
                  e.currentTarget.style.backgroundColor = "#E5E7EB";
              }}
              onMouseLeave={(e) => {
                if (!isResetLoading)
                  e.currentTarget.style.backgroundColor = "#F3F4F6";
              }}
            >
              {t.cancel}
            </button>

            {/* Submit */}
            <button
              type="button"
              onClick={onSubmit}
              disabled={submitDisabled}
              style={{
                ...styles.modalButton,
                ...styles.modalPrimaryButton,
                opacity: submitDisabled ? 0.7 : 1,
                cursor: submitDisabled ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!submitDisabled) {
                  e.currentTarget.style.backgroundColor = "#063a73";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(8, 77, 152, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (!submitDisabled) {
                  e.currentTarget.style.backgroundColor = "#084d98";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
            >
              {isResetLoading ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  {t.sending}
                </>
              ) : resetStatus === "success" ? (
                <>
                  <CheckCircle size={18} />
                  {t.sentSuccessfully}
                </>
              ) : cooldownActive ? (
                `${t.resendIn || "Resend in"} ${resendCooldownSeconds}s`
              ) : (
                t.sendResetLink
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;